import {
  byMediaActionOrder,
  useMediaVerbs,
  type MediaActionControl,
  type MediaMenuAction,
  type MediaMenuActionItem,
} from '@lib/media/useMediaVerbs';
import { Media } from '@typings/Media';
import { useCallback } from 'react';
import { isAdminAction, useMediaAdminActions } from './useMediaAdminActions';

/**
 * Every media action, for a surface that has the library page's providers.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS NOW A COMPOSITION OF TWO HOOKS. MOUNT IT ONLY UNDER `LibraryPage`.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * - `useMediaVerbs` (`@lib/media`) — the three verbs plus copy-link, open-original and
 *   download. Safe on any route.
 * - `useMediaAdminActions` — rename, retry and delete. Needs `LibraryFolderProvider` and
 *   `MediaDeleteModalProvider`, so calling it outside the library page throws.
 *
 * Because the second half throws off-route, this composed hook does too. A surface that renders
 * on more than one route (`MomentModal` serves the library page, `/search` and `/atlas` from one
 * component) must call `useMediaVerbs` directly and offer the smaller menu, OR mount a component
 * that calls this behind a condition — a condition may guard a *component*, never a hook.
 *
 * The return shape is unchanged from before the split, deliberately: `VideoMenuButton`,
 * `MediaVerbRail`, `MenuCell`, `TitleCell` and `MobileMediaTableList` were not touched.
 */
export function useMediaMenuActions(
  media: Media,
  isModifiable: boolean,
  onAfterDestructiveAction?: () => void,
  /** Frozen playhead, for the "Copy link at 1:30" row. See `useMediaVerbs`. */
  playheadTime?: number,
) {
  // No `surface`: this hook only mounts under the library page, so the default is correct.
  const { actions: verbActions, executeVerb } = useMediaVerbs(media, { playheadTime });
  const {
    actions: adminActions,
    executeAdminAction,
    mediaRenameModalRef,
    isRenameModalOpen,
    setIsRenameModalOpen,
  } = useMediaAdminActions(media, isModifiable, onAfterDestructiveAction);

  // Merged then SORTED, not concatenated: `rename` sits between `open-original` and `download`,
  // which live in the other half. See `MEDIA_ACTION_ORDER`.
  const actions: MediaMenuActionItem[] = [...verbActions, ...adminActions].sort(byMediaActionOrder);

  const executeAction = useCallback(
    async (action: MediaMenuAction, control: MediaActionControl = 'media-kebab') => {
      // Routed by `isAdminAction` rather than by a second copy of the id list, so adding an
      // action to one half can't silently route to the other's no-op default branch.
      if (isAdminAction(action)) {
        await executeAdminAction(action, control);
      } else {
        await executeVerb(action, control);
      }
    },
    [executeAdminAction, executeVerb],
  );

  return {
    actions,
    executeAction,
    mediaRenameModalRef,
    isRenameModalOpen,
    setIsRenameModalOpen,
  };
}
